import React, { useState } from "react";
import { IconButton, Box, Drawer, Avatar, Typography } from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  Segment,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { handleScroll } from "pages/landing";

// Define keyframe animations
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export default function MobileAppBar({ section }) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const navigate = useNavigate();

  return (
    <>
      <IconButton
        onClick={toggleMenu}
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          background: "transparent",
          transition: "all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          transform: isOpen
            ? "rotate(90deg) scale(1.1)"
            : "rotate(0deg) scale(1)",
          boxShadow: isOpen ? "0 8px 24px rgba(102, 126, 234, 0.4)" : "none",
          "&:hover": {
            background: "rgba(102, 126, 234, 0.1)",
          },
        }}
      >
        <Box sx={{ position: "relative", width: 28, height: 28 }}>
          {section === "product" ? (
            <AppsIcon
              sx={{
                position: "absolute",
                inset: 0,
                color: isOpen ? "white" : "black",
                fontSize: 28,
                transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                opacity: isOpen ? 0 : 1,
                transform: isOpen
                  ? "rotate(180deg) scale(0)"
                  : "rotate(0deg) scale(1)",
              }}
            />
          ) : (
            <Segment
              sx={{
                position: "absolute",
                inset: 0,
                color: isOpen ? "white" : "black",
                fontSize: 28,
                transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                opacity: isOpen ? 0 : 1,
                transform: isOpen
                  ? "rotate(180deg) scale(0)"
                  : "rotate(0deg) scale(1)",
              }}
            />
          )}
        </Box>
      </IconButton>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleMenu}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
            background: "white",
            backdropFilter: "blur(10px)",
            position: "relative",
            overflow: "hidden",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            display: "none",
          },
          [`& .MuiDrawer-paper`]: {
            borderRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "10%",
            width: "300px",
            height: "300px",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
            borderRadius: "50%",
            animation: `${rotate} 20s linear infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "5%",
            width: "200px",
            height: "200px",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            animation: `${rotate} 15s linear infinite reverse`,
          }}
        />

        {/* Menu Content */}
        <Box>
          <IconButton onClick={toggleMenu}>
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 3,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                animation: `${fadeInUp} 0.5s ease-out 0.1s both`,
              }}
            >
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: "white",
                  color: "#0053fd",
                  margin: "0 auto 1rem",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              >
                <img src="/images/ef-logo.svg" />
              </Avatar>
              <Box>
                {section === "product" ? (
                  <Box sx={{ display: "grid", gap: "4rem", marginTop: "4rem" }}>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigate("/contact-us");
                        toggleMenu();
                      }}
                    >
                      Contact Us
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        navigate("/about-us");
                      }}
                    >
                      About Us
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "grid", gap: "4rem", marginTop: "4rem" }}>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        handleScroll("ourproduct");
                      }}
                    >
                      Our Products
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        handleScroll("howitworks", -100);
                      }}
                    >
                      How it works
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        handleScroll("featuresbenefits");
                      }}
                    >
                      Features
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        handleScroll("whyus");
                      }}
                    >
                      Why
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        toggleMenu();
                        handleScroll("faqs");
                      }}
                    >
                      FAQs
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
